// Shape geometry computation and morphing for 3D harmonic visualization
import * as THREE from 'three/webgpu';
import type { PlaneData, ShapeData } from './types';

const MAXF = 14;

export function computePlanes(geo: THREE.BufferGeometry): PlaneData[] {
  const g = geo.index ? geo.toNonIndexed() : geo;
  const pos = g.getAttribute('position');
  const A = new THREE.Vector3(), B = new THREE.Vector3(), C = new THREE.Vector3();
  const N = new THREE.Vector3(), AB = new THREE.Vector3(), AC = new THREE.Vector3();
  const planes: PlaneData[] = [];

  for (let i = 0; i < pos.count; i += 3) {
    A.fromBufferAttribute(pos, i);
    B.fromBufferAttribute(pos, i + 1);
    C.fromBufferAttribute(pos, i + 2);
    AB.subVectors(B, A);
    AC.subVectors(C, A);
    N.crossVectors(AB, AC);
    if (N.lengthSq() < 1e-10) continue;
    N.normalize();
    let d = N.dot(A);
    if (d < 0) { N.negate(); d = -d; }
    if (d < 1e-4) continue;

    let dup = false;
    for (const p of planes) {
      if (p.n.x * N.x + p.n.y * N.y + p.n.z * N.z > 0.9995 && Math.abs(p.d - d) < 0.01) {
        dup = true;
        break;
      }
    }
    if (!dup) planes.push({ n: { x: N.x, y: N.y, z: N.z }, d });
  }
  return planes;
}

export function shapeData(key: string): ShapeData {
  if (key === 'sphere') return { planes: [], count: 0, sphere: 1, r: 0.42 };

  let geo: THREE.BufferGeometry;
  switch (key) {
    case "2": geo = new THREE.ConeGeometry(0.48, 0.76, 3); break;
    case "3": geo = new THREE.ConeGeometry(0.50, 0.76, 5); break;
    case "5": geo = new THREE.DodecahedronGeometry(0.47); break;
    case "6": geo = new THREE.CylinderGeometry(0.42, 0.42, 0.64, 6); break;
    case "7": geo = new THREE.ConeGeometry(0.50, 0.80, 7); break;
    case "4":
    default: geo = new THREE.BoxGeometry(0.56, 0.56, 0.56); break;
  }

  const planes = computePlanes(geo);
  return { planes, count: planes.length, sphere: 0, r: 0.42 };
}

export function buildShapes(): Record<string, ShapeData> {
  const SHAPES: Record<string, ShapeData> = {};
  ['2', '3', '4', '5', '6', '7', 'sphere'].forEach(k => {
    SHAPES[k] = shapeData(k);
  });
  return SHAPES;
}

export function loadShape(
  arr: THREE.Vector4[],
  cU: { value: number },
  sU: { value: number },
  rU: { value: number },
  data: ShapeData
) {
  for (let i = 0; i < MAXF; i++) {
    if (i < data.count) {
      const p = data.planes[i];
      arr[i].set(p.n.x, p.n.y, p.n.z, p.d);
    } else {
      arr[i].set(0, 0, 0, 0);
    }
  }
  cU.value = data.count;
  sU.value = data.sphere;
  rU.value = data.r;
}

export { MAXF };